import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as sessionNotesService from '../../services/sessionNotes';
import * as franchiseService from '../../services/franchise';
import * as centreService from '../../services/centre';
import * as studentService from '../../services/student';

interface StudentNote {
  student_id: string;
  student_name: string;
  subject_id: string;
  attended_session: boolean;
  homework_status: string; // 'na', 'completed', 'completed_and_marked', 'not_completed'
  session_note: string;
}

const AddSessionNote: React.FC = () => {
  const navigate = useNavigate();
  const canAccess = useSelector((state: any) => state.auth.permissions?.['session-notes']);
  
  const [loading, setLoading] = useState(false);
  const [franchises, setFranchises] = useState<any[]>([]);
  const [centres, setCentres] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [studentNotes, setStudentNotes] = useState<StudentNote[]>([]);
  const [studentNotesErrors, setStudentNotesErrors] = useState<Record<string, { session_note?: string }>>({});

  const validationSchema = Yup.object({
    session_date: Yup.string().required('Session date is required'),
    franchise_id: Yup.string().required('Franchise is required'),
    subject_id: Yup.string().required('Subject is required'),
  });

  const formik = useFormik({
    initialValues: {
      session_date: '',
      franchise_id: '',
      centre_id: '',
      subject_id: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      // This will be called by handleSubmit
    }
  });

  useEffect(() => {
    fetchFranchises();
  }, []);

  useEffect(() => {
    // Auto-select franchise if only one is available
    if (franchises.length === 1 && !formik.values.franchise_id) {
      formik.setFieldValue('franchise_id', franchises[0].id.toString());
      const selectedFranchise = franchises[0];
      setReviewEnabled(selectedFranchise?.enabled_review_session_notes || false);
    }
  }, [franchises]);

  useEffect(() => {
    // Auto-select centre if only one is available
    if (centres.length === 1 && !formik.values.centre_id) {
      formik.setFieldValue('centre_id', centres[0].id.toString());
    }
  }, [centres]);

  useEffect(() => {
    if (formik.values.franchise_id) {
      fetchCentres(parseInt(formik.values.franchise_id));
      fetchSubjects(parseInt(formik.values.franchise_id));
    } else {
      setCentres([]);
      setSubjects([]);
      formik.setFieldValue('centre_id', '');
      formik.setFieldValue('subject_id', '');
    }
  }, [formik.values.franchise_id]);

  useEffect(() => {
    if (formik.values.franchise_id && formik.values.subject_id) {
      fetchStudents();
    } else {
      setStudents([]);
      setStudentNotes([]);
    }
  }, [formik.values.centre_id, formik.values.subject_id, formik.values.franchise_id]);

  const fetchFranchises = async () => {
    try {
      const response = await franchiseService.getAllActiveFranchise();
      if (response?.success) {
        setFranchises(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch franchises');
    }
  };

  const fetchCentres = async (franchiseId: number) => {
    try {
      const response = await centreService.getCentresByFranchiseId(franchiseId);
      if (response?.success) {
        setCentres(response.collections || []);
      }
    } catch (error) {
      toast.error('Failed to fetch centres');
    }
  };

  const fetchSubjects = async (franchiseId: number) => {
    try {
      const response = await franchiseService.getSubjectsByFranchise(franchiseId);
      if (response?.success) {
        setSubjects(response?.collections.filter((sub) => sub.status == 'active') || []);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await studentService.getStudentsByFranchiseCentreSubject(
        parseInt(formik.values.franchise_id),
        formik.values.centre_id ? parseInt(formik.values.centre_id) : null,
        parseInt(formik.values.subject_id)
      );
      if (response?.success) {
        const studentList = response.collections || [];
        setStudents(studentList);
        
        // Initialize student notes with student name from first_name and last_name
        const initialNotes: StudentNote[] = studentList.map((student: any) => ({
          student_id: student.id.toString(),
          student_name: `${student.first_name} ${student.last_name}`.trim(),
          subject_id: formik.values.subject_id,
          attended_session: true,
          homework_status: 'na',
          session_note: ''
        }));
        setStudentNotes(initialNotes);
      }
    } catch (error) {
      toast.error('Failed to fetch students');
    }
  };

  const handleStudentNoteChange = (studentId: string, field: string, value: any) => {
    setStudentNotes(prev =>
      prev.map(note =>
        note.student_id === studentId ? { ...note, [field]: value } : note
      )
    );
    
    // Clear error for session_note when user types
    if (field === 'session_note' && value.trim() !== '') {
      setStudentNotesErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[studentId];
        return newErrors;
      });
    }
  };

  const handleMarkAllAttended = (attended: boolean) => {
    setStudentNotes(prev =>
      prev.map(note => ({
        ...note,
        attended_session: attended,
        // Reset other fields if marking as not attended
        homework_status: attended ? note.homework_status : 'na',
        session_note: attended ? note.session_note : ''
      }))
    );
    
    // Clear all errors when marking all as not attended
    if (!attended) {
      setStudentNotesErrors({});
    }
  };

  const isAllAttended = studentNotes.length > 0 && studentNotes.every(note => note.attended_session);
  const isSomeAttended = studentNotes.some(note => note.attended_session) && !isAllAttended;

  const validateStudentNotes = () => {
    const errors: Record<string, { session_note?: string }> = {};
    let hasErrors = false;
    const attendedStudents = studentNotes.filter(note => note.attended_session);

    for (const note of attendedStudents) {
      if (!note.session_note || note.session_note.trim() === '') {
        errors[note.student_id] = { session_note: 'Session note is required' };
        hasErrors = true;
      }
    }

    setStudentNotesErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async (status: 'draft' | 'review_pending' | 'published') => {
    // Validate form fields
    const errors = await formik.validateForm();
    formik.setTouched({
      session_date: true,
      franchise_id: true,
      subject_id: true
    });
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (studentNotes.length === 0) {
      toast.error('No students found for the selected criteria');
      return;
    }

    // Validate student notes - only session note is required for attended students
    if (!validateStudentNotes()) {
      return;
    }

    setLoading(true);
    try {
      // Transform student notes to match new API structure
      const students = studentNotes.map(note => {
        return {
          student_id: parseInt(note.student_id),
          attended: note.attended_session ? 'yes' : 'no',
          homework_status: note.homework_status,
          session_note: note.session_note
        };
      });

      const payload = {
        session_date: formik.values.session_date,
        franchise_id: parseInt(formik.values.franchise_id),
        centre_id: formik.values.centre_id ? parseInt(formik.values.centre_id) : '',
        status: status,
        subjects: [
          {
            subject_id: parseInt(formik.values.subject_id),
            students: students
          }
        ]
      };

      const response = await sessionNotesService.createSessionNote(payload);
      if (response?.success) {
        toast.success('Session note created successfully');
        navigate('/session-notes');
      } else {
        toast.error(response?.message || 'Failed to create session note');
      }
    } catch (error) {
      toast.error('Failed to create session note');
    } finally {
      setLoading(false);
    }
  };

  if (!canAccess?.create) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              You do not have permission to create session notes.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Add Session Note</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/session-notes">Session Notes</Link>
                </li>
                <li className="breadcrumb-item active">Add</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Session Details</h3>
              <div className="card-tools">
                <Link to="/session-notes" className="btn btn-sm btn-default">
                  <i className="fas fa-arrow-left mr-1"></i>
                  Back to List
                </Link>
              </div>
            </div>
            <div className="card-body">
              {/* Info text for selected franchise and centre */}
              {franchises.length === 1 && centres.length === 1 && formik.values.franchise_id && formik.values.centre_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === formik.values.franchise_id)?.name}
                  <span className="mx-2">•</span>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === formik.values.centre_id)?.name}
                </div>
              )}
              {franchises.length === 1 && centres.length > 1 && formik.values.franchise_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Franchise:</strong> {franchises.find(f => f.id.toString() === formik.values.franchise_id)?.name}
                </div>
              )}
              {franchises.length > 1 && centres.length === 1 && formik.values.franchise_id && formik.values.centre_id && (
                <div className="mb-3 p-2" style={{ fontSize: '14px', color: '#17a2b8', backgroundColor: '#e7f6f8', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  <strong>Centre:</strong> {centres.find(c => c.id.toString() === formik.values.centre_id)?.name}
                </div>
              )}
              
              <div className="row">
                <div className={franchises.length > 1 || centres.length > 1 ? "col-md-3" : "col-md-4"}>
                  <div className="form-group">
                    <label>
                      Session Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      name="session_date"
                      className={`form-control ${formik.touched.session_date && formik.errors.session_date ? 'is-invalid' : ''}`}
                      value={formik.values.session_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      max={new Date().toISOString().split('T')[0]}
                    />
                    {formik.touched.session_date && formik.errors.session_date && (
                      <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                        {formik.errors.session_date}
                      </div>
                    )}
                  </div>
                </div>

                {franchises.length > 1 && (
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>
                        Franchise <span className="text-danger">*</span>
                      </label>
                      <Select
                        name="franchise_id"
                        placeholder="Search By Franchise"
                        options={franchises}
                        getOptionValue={(option) => `${option['id']}`}
                        getOptionLabel={(option) => option['name']}
                        onChange={(option) => {
                          if (option) {
                            formik.setFieldValue('franchise_id', option.id.toString());
                            const selectedFranchise = franchises.find(f => f.id === option.id);
                            setReviewEnabled(selectedFranchise?.enabled_review_session_notes || false);
                          } else {
                            formik.setFieldValue('franchise_id', '');
                            setReviewEnabled(false);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched('franchise_id', true)}
                        isClearable={true}
                        isSearchable={true}
                        styles={{
                          control: (provided: any, state: any) => ({
                            ...provided,
                            borderColor: formik.touched.franchise_id && formik.errors.franchise_id ? '#dc3545' : provided.borderColor,
                            '&:hover': {
                              borderColor: formik.touched.franchise_id && formik.errors.franchise_id ? '#dc3545' : provided.borderColor
                            }
                          })
                        }}
                      />
                      {formik.touched.franchise_id && formik.errors.franchise_id && (
                        <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                          {formik.errors.franchise_id}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {centres.length > 1 && (
                  <div className={franchises.length > 1 ? "col-md-3" : "col-md-4"}>
                    <div className="form-group">
                      <label>Centre</label>
                      <select
                        name="centre_id"
                        className="form-control"
                        value={formik.values.centre_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={!formik.values.franchise_id}
                      >
                        <option value="">Select Centre</option>
                        {centres.map(centre => (
                          <option key={centre.id} value={centre.id}>
                            {centre.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className={
                  franchises.length === 1 && centres.length === 1 ? "col-md-4" :
                  franchises.length > 1 && centres.length > 1 ? "col-md-3" :
                  franchises.length > 1 || centres.length > 1 ? "col-md-4" : "col-md-4"
                }>
                  <div className="form-group">
                    <label>
                      Subject <span className="text-danger">*</span>
                    </label>
                    <select
                      name="subject_id"
                      className={`form-control ${formik.touched.subject_id && formik.errors.subject_id ? 'is-invalid' : ''}`}
                      value={formik.values.subject_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={!formik.values.franchise_id}
                    >
                      <option value="">Select Subject</option>
                      {subjects.map(subject => (
                        <option key={subject.subject_id} value={subject.subject_id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                    {formik.touched.subject_id && formik.errors.subject_id && (
                      <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                        {formik.errors.subject_id}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Student Notes */}
          {studentNotes.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Student Session Notes</h3>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>Sr.</th>
                        <th style={{ width: '100px' }} className="text-center">
                          <div className="d-flex align-items-center justify-content-center">
                            <input
                              type="checkbox"
                              checked={isAllAttended}
                              ref={(input) => {
                                if (input) {
                                  input.indeterminate = isSomeAttended;
                                }
                              }}
                              onChange={(e) => handleMarkAllAttended(e.target.checked)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer', marginRight: '5px' }}
                              title={isAllAttended ? 'Unmark all' : 'Mark all attended'}
                            />
                            <span>Attended</span>
                          </div>
                        </th>
                        <th style={{ width: '200px' }}>Student Name</th>
                        <th style={{ width: '180px' }}>Homework</th>
                        <th>Session Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentNotes.map((note, index) => (
                        <tr key={note.student_id}>
                          <td>{index + 1}</td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={note.attended_session}
                              onChange={(e) =>
                                handleStudentNoteChange(
                                  note.student_id,
                                  'attended_session',
                                  e.target.checked
                                )
                              }
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </td>
                          <td>{note.student_name}</td>
                          <td>
                            <select
                              className="form-control form-control-sm"
                              value={note.homework_status}
                              onChange={(e) =>
                                handleStudentNoteChange(
                                  note.student_id,
                                  'homework_status',
                                  e.target.value
                                )
                              }
                              disabled={!note.attended_session}
                            >
                              <option value="na">N/A</option>
                              <option value="completed">Completed</option>
                              <option value="completed_and_marked">Completed and Marked</option>
                              <option value="not_completed">Not Completed</option>
                            </select>
                          </td>
                          <td>
                            <textarea
                              className={`form-control form-control-sm ${studentNotesErrors[note.student_id]?.session_note ? 'is-invalid' : ''}`}
                              rows={2}
                              value={note.session_note}
                              onChange={(e) =>
                                handleStudentNoteChange(
                                  note.student_id,
                                  'session_note',
                                  e.target.value
                                )
                              }
                              disabled={!note.attended_session}
                              placeholder="Enter session note..."
                            />
                            {studentNotesErrors[note.student_id]?.session_note && (
                              <div className="invalid-feedback d-block" style={{ fontSize: '14px' }}>
                                {studentNotesErrors[note.student_id].session_note}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={() => handleSubmit('draft')}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save as Draft'}
                </button>
                {reviewEnabled ? (
                  <button
                    type="button"
                    className="btn btn-success mr-2"
                    onClick={() => handleSubmit('review_pending')}
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit for Review'}
                  </button>
                ) : (
                  canAccess?.publish && (
                    <button
                      type="button"
                      className="btn btn-primary mr-2"
                      onClick={() => handleSubmit('published')}
                      disabled={loading}
                    >
                      {loading ? 'Publishing...' : 'Publish'}
                    </button>
                  )
                )}
                <Link to="/session-notes" className="btn btn-default">
                  Cancel
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AddSessionNote;
